import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { HourglassIcon } from "@phosphor-icons/react/dist/csr/Hourglass";
import { LinkBreakIcon } from "@phosphor-icons/react/dist/csr/LinkBreak";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs, TabsList, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { Text } from "@volca/ui/components/ui/text";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import {
  referenceKey,
  StatementRenderer,
  type StatementItemReference,
} from "@/payroll/ai-script/statement/statement-renderer";
import { STATEMENT_RELEASE_REASON } from "@/payroll/ai-script/types";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { useGetPackThreads } from "@/payroll/pack-chat/query";
import { PACK_CHAT_STATUS } from "@/payroll/pack-chat/types";
import { usePaypacksAccess } from "@/payroll/pay-breakdown/payroll-emails-access";
import { PayPeriodLockedMark } from "@/payroll/pay-periods/pay-period-locked";
import { AiScriptTechnicianFeedbackForm } from "./ai-script-technician-feedback-form";
import { PackQuestionHistory } from "./pack-question-history";
import { PayApprovalPanel } from "./pay-approval-panel";
import { TechnicianStatementMessage } from "./technician-statement-message";

interface TechnicianStatementViewProps {
  businessId: string | null;
  runId: string;
  isLocked: boolean;
}

const PACK_TAB = {
  pay: "pay",
  questions: "questions",
} as const;

type PackTab = (typeof PACK_TAB)[keyof typeof PACK_TAB];

const TAB_LABELS: Record<PackTab, string> = {
  [PACK_TAB.pay]: "My pay",
  [PACK_TAB.questions]: "Questions",
};

/**
 * Two tabs over one pack: the pay, and the questions asked about it.
 *
 * The questions used to live under the statement, which on a real pack is
 * several screens down — so a technician with a reply waiting had to scroll the
 * whole length of their own pay to find it, and had no way of knowing it was
 * there. A tab says it in the one place they always see, and going back to the
 * figures is one tap either way.
 */
const PackTabs = ({
  value,
  onChange,
  openCount,
}: {
  value: PackTab;
  onChange: (next: PackTab) => void;
  openCount: number;
}) => (
  <Tabs
    value={value}
    onValueChange={(next) => onChange(next as PackTab)}
    className="w-full"
  >
    <TabsList aria-label="Your pack" className="w-full">
      {Object.values(PACK_TAB).map((tab) => (
        <TabsTrigger key={tab} value={tab} className="text-xs">
          {TAB_LABELS[tab]}
          {/* A dot, not a count badge: the only thing worth interrupting someone's
            pay for is that something of theirs is still open. */}
          {tab === PACK_TAB.questions && openCount > 0 && (
            <>
              <span
                className="block size-[7px] rounded-full bg-accent-amber-bold"
                aria-hidden
              />
              <span className="sr-only">
                {openCount} open {openCount === 1 ? "question" : "questions"}
              </span>
            </>
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  </Tabs>
);

// The technician-facing surface. It reads ONLY the per-technician statement
// endpoint (never the raw workbook), so a technician on a shared department
// sheet can't see anyone else's rows.
export const TechnicianStatementView = ({
  businessId,
  runId,
  isLocked,
}: TechnicianStatementViewProps) => {
  const { data, isLoading, error } = useGetAiScriptStatement(
    businessId,
    runId,
    { identity: STATEMENT_IDENTITY.viewer, enabled: true },
  );
  // Held here rather than in either child: the statement raises the figure and the
  // form consumes it.
  const [attached, setAttached] = useState<StatementItemReference | null>(null);
  const [sentThreadId, setSentThreadId] = useState<string | null>(null);
  const [tab, setTab] = useState<PackTab>(PACK_TAB.pay);
  // The same gate the manager side reads, because the server enforces it on both:
  // a business without saved PayPacks access has no PackChat, and its endpoints 404.
  //
  // Without this a technician at such a business was invited to ask about every
  // figure on their pay and told "PackChat couldn't be loaded. Contact support" when
  // they opened the tab — which is the worst thing a payroll screen can say to
  // somebody about their own money, for a feature nobody had turned on.
  const { enabled: packChatEnabled } = usePaypacksAccess();

  // Questions belong to the run the FIGURES came from. After a rerun the endpoint
  // serves the run their pay was sent from, so asking about the requested run would
  // file a question under one run while this tab reads another — and the question a
  // technician just sent would vanish. Falls back to the requested run for an api too
  // old to report it, which is what such an api served anyway.
  const statementRunId = data?.statement_run_id ?? runId;

  // Withheld pay has no threads to read, and the server refuses them for exactly the
  // reason it refuses the statement — so asking anyway is a 404 on every open. Waits
  // for the statement to come back released before it asks at all.
  const packChatReady = packChatEnabled && !!data && data.released !== false;

  // Fetched here rather than inside the questions tab: the tab itself has to say
  // whether anything is open, and it says that whichever tab is showing.
  const threadsQuery = useGetPackThreads(
    businessId,
    statementRunId,
    packChatReady,
  );
  // Read these during render so the tab handler receives changes in freshness.
  const { isStale: threadsAreStale, isFetching: threadsAreFetching } =
    threadsQuery;
  const threads = threadsQuery.data?.threads ?? [];
  const openCount = threads.filter(
    (thread) => thread.status === PACK_CHAT_STATUS.open,
  ).length;

  const handleTabChange = (next: PackTab) => {
    if (next === tab) return;
    setTab(next);
    // The badge keeps this query mounted while My pay is open.
    if (next === PACK_TAB.questions && threadsAreStale && !threadsAreFetching) {
      void threadsQuery.refetch();
    }
  };

  if (isLoading) {
    return (
      <Stack gap={4} align="center" justify="center" className="h-full w-full">
        <Spinner layout="centered" />
      </Stack>
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Statement unavailable"
        description="We couldn't load your statement. Please try again shortly."
      />
    );
  }

  // The requester is a technician we couldn't link to their ServiceTitan record.
  if (data?.linked === false) {
    return (
      <TechnicianStatementMessage
        icon={<LinkBreakIcon aria-hidden />}
        title="Your account isn't linked yet"
        body="We couldn't match your login to your technician record. Please contact your admin so they can link it."
      />
    );
  }

  // Payroll has run but this technician's pay is not theirs to see yet, so the
  // figures are still liable to move. Said plainly and BEFORE the empty check:
  // routed there instead, a technician reads "No pay this period" for pay that
  // exists — the alarming version of the same wait. `=== false` on purpose, so an
  // api that predates the gate reads as released rather than withholding everything.
  if (data?.released === false) {
    // Which wait it is matters. Told to expect a send, a technician at a business
    // that never emails PayPacks waits on nothing and chases an email nobody sent;
    // told the period is still running when it closed last week, they distrust the
    // screen.
    return data.release_reason === STATEMENT_RELEASE_REASON.periodOpen ? (
      <TechnicianStatementMessage
        icon={<HourglassIcon aria-hidden />}
        title="This pay period isn't finished yet"
        body="Your statement will appear here once the pay period ends and your pay is worked out."
      />
    ) : (
      <TechnicianStatementMessage
        icon={<HourglassIcon aria-hidden />}
        title="Your pay isn't ready yet"
        body="Your manager is still finalizing payroll for this period. Your statement will appear here as soon as it's published."
      />
    );
  }

  const statement = data?.statement;
  const isEmpty = !statement || (statement.blocks?.length ?? 0) === 0;
  if (isEmpty) {
    return (
      <TechnicianStatementMessage
        icon={<FileTextIcon aria-hidden />}
        title="No pay this period"
        body="There's nothing on your statement for this pay period yet."
      />
    );
  }

  const approvalPanel = (
    <PayApprovalPanel
      businessId={businessId}
      runId={statementRunId}
      statement={data}
      isLocked={isLocked}
    />
  );

  // Without PayPacks, this is a pay statement and nothing else: no tabs to switch,
  // no flag beside each figure, no box to send a question nobody would receive — and
  // no sign-off, which rides on the same saved selection. Approving emails the office, so a
  // business that has been told nothing will send does not get a button that sends
  // something.
  if (!packChatEnabled) {
    return (
      <Stack gap={0} align="stretch" className="h-full min-h-0 w-full">
        <Stack gap={0} align="stretch" className="min-h-0 flex-1">
          <StatementRenderer statement={statement} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={0} align="stretch" className="h-full min-h-0 w-full">
      <Stack align="start" gap={0} className="w-full shrink-0">
        <Stack
          align="start"
          gap={0}
          className="mx-auto w-full max-w-2xl px-4 pt-4 pb-3"
        >
          <PackTabs
            value={tab}
            onChange={handleTabChange}
            openCount={openCount}
          />
        </Stack>
      </Stack>

      <Stack gap={0} align="stretch" className="min-h-0 flex-1">
        {tab === PACK_TAB.pay ? (
          <StatementRenderer
            statement={statement}
            itemAction={
              isLocked
                ? undefined
                : {
                    onAction: setAttached,
                    activeKey: attached ? referenceKey(attached) : null,
                  }
            }
            // Agreeing comes first and asking second: a technician who has read
            // to the bottom of their own pay is usually done, and the question
            // box is for the times they are not.
            //
            // The wrapper this sits in is align="stretch" for a reason — see
            // technician-workbook-experience. Without it the pack is content-sized,
            // so swapping the full-width approve button for a line of text narrowed
            // the whole statement by ~150px under the technician's finger.
            footer={
              <>
                {approvalPanel}
                {isLocked ? (
                  <Stack
                    direction="row"
                    align="center"
                    gap={2}
                    className="border-t border-line pt-5"
                  >
                    <PayPeriodLockedMark />
                    <Text variant="caption" tone="muted">
                      This pay period is locked. You can still read and reply to
                      existing questions.
                    </Text>
                  </Stack>
                ) : !threadsQuery.data ? (
                  <PackChatState
                    layout="collection"
                    loading={!threadsQuery.error}
                    error={threadsQuery.error}
                    title="Questions couldn't be loaded"
                    action={
                      threadsQuery.error ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void threadsQuery.refetch()}
                        >
                          Try again
                        </Button>
                      ) : undefined
                    }
                  />
                ) : (
                  <AiScriptTechnicianFeedbackForm
                    businessId={businessId}
                    runId={statementRunId}
                    aiEnabled={threadsQuery.data?.ai_enabled ?? false}
                    attached={attached}
                    onClearAttached={() => setAttached(null)}
                    // Sending moves them to the conversation they just started,
                    // so the reply lands somewhere they have already been.
                    onSent={(threadId) => {
                      setSentThreadId(threadId);
                      setTab(PACK_TAB.questions);
                    }}
                  />
                )}
              </>
            }
          />
        ) : (
          <Stack
            align="start"
            gap={0}
            data-pack-question-scroll
            className="quiet-scrollbar h-full min-h-0 w-full overflow-y-auto pb-6"
          >
            <Stack
              align="start"
              gap={0}
              className="mx-auto w-full max-w-2xl px-4 py-5"
            >
              <PackQuestionHistory
                businessId={businessId}
                runId={statementRunId}
                threads={threads}
                sentThreadId={sentThreadId}
                loading={threadsQuery.isLoading}
                error={threadsQuery.error}
                onRetry={() => void threadsQuery.refetch()}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
